// src/pages/PromotionLetter.js
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import html2pdf from 'html2pdf.js';
import '../letterstyles/PromotionLetter.css';

const PromotionLetter = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const printRef = useRef(null);

  const user = state?.user;
  const superAdminId = Number(localStorage.getItem('adminuserId')) || 0;

  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);

  // Redirect if no user data
  useEffect(() => {
    if (!user) {
      alert("No employee data provided");
      navigate('/');
    }
  }, [user, navigate]);

  // Fetch company details
  useEffect(() => {
    const fetchCompany = async () => {
      try {
        setLoading(true);
        const res = await fetch(`https://payyug.in/api/get-company?superAdminId=${superAdminId}`);
        const data = await res.json();
        if (res.ok && data) {
          if (data.logo) {
            data.logoUrl = `https://payyug.in/${data.logo.replace(/^images\//, '')}`;
          }
          setCompany(data);
        } else {
          console.error("Failed to fetch company details");
        }
      } catch (err) {
        console.error("Error fetching company details:", err);
      } finally {
        setLoading(false);
      }
    };
    if (superAdminId) fetchCompany();
  }, [superAdminId]);

  // Print handler
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `${user?.fullName || 'Employee'}_Promotion_Letter`,
  });

  // PDF Download
  const handleDownload = () => {
    if (!printRef.current) return;

    const element = printRef.current;
    const opt = {
      margin: 0,
      filename: `${user?.fullName || 'Employee'}_Promotion_Letter.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: {
        scale: 2.5,
        useCORS: true,
        allowTaint: true,
        scrollY: 0,
        logging: false,
        letterRendering: true,
      },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait', compressPdf: true },
      pagebreak: {
        mode: ['avoid-all', 'css', 'legacy'],
        avoid: ['.pl-doc-header', '.pl-doc-sign-block', '.pl-doc-footer'],
      },
    };

    html2pdf().set(opt).from(element).save();
  };

  if (!user) return null;
  if (loading) return <p className="pl-doc-loading">Loading company details...</p>;

  return (
    <div className="pl-doc-wrapper">
      <div className="pl-doc-actions">
        <button type="button" className="pl-doc-action-button" onClick={handlePrint}>
          Print
        </button>
        <button type="button" className="pl-doc-action-button" onClick={handleDownload}>
          Download
        </button>
      </div>

      <div className="pl-doc-sheet" ref={printRef}>
        {/* Header */}
        <div className="pl-doc-header">
          <img
            src={company?.logoUrl || '/default-logo.png'}
            alt="Company Logo"
            className="pl-doc-logo"
          />
          <div className="pl-doc-company-info">
            <h2>{company?.name}</h2>
            <p><strong>CIN:</strong> {company?.cin || 'N/A'}</p>
            <p><strong>Regt. Office:</strong> {company?.address || 'N/A'}</p>
            <p><strong>Email:</strong> {company?.email || 'N/A'}</p>
            <p><strong>Contact:</strong> {company?.contact || 'N/A'}</p>
          </div>
        </div>

        {/* Promotion Letter Heading */}
        <div className="pl-doc-title-wrapper">
          <h2>Promotion Letter</h2>
        </div>

        {/* Body */}
        <div className="pl-doc-body">
          <p className="pl-doc-date">
            <strong>Date:</strong> {new Date().toLocaleDateString('en-GB')}
          </p>

          <p>To,</p>
          <p><strong>{user.fullName}</strong></p>
          <p>{user.branchName || user.branch}</p>
          <p><strong>Employee ID:</strong> {user.userId}</p>

          <p className="pl-doc-subject">
            <strong>Subject:</strong> Promotion & Professional Experience Confirmation
          </p>

          <p>Dear {user.fullName},</p>

          <p>
            We are delighted to formally acknowledge your professional growth and promotion at <strong>{company?.name}</strong>. 
            You have been an integral part of our team at the <strong>{user.branchName || user.branch}</strong> branch, serving as <strong>{user.roleName || user.role}</strong>.
          </p>

          <p>
            Your consistent dedication, exemplary performance, and professional conduct have been instrumental in the success of our organization. 
            Effective from <strong>{new Date().toLocaleDateString('en-GB')}</strong>, you are hereby promoted to the role of <strong>{user.newRole || 'Senior ' + (user.roleName || user.role)}</strong>.
          </p>

          <p>
            We trust you will continue to uphold the standards of excellence and contribute meaningfully to the company’s vision and objectives.
          </p>

          <p className="pl-doc-congrats">
            <strong>We congratulate you on this achievement and look forward to your continued success!</strong>
          </p>

          <div className="pl-doc-sign-block">
            <p>Sincerely,</p>
            <p className="pl-doc-signer">
              HR Manager<br />
              {company?.name}
            </p>
          </div>
        </div>

        {/* Footer pinned to bottom */}
        <div className="pl-doc-footer">
          <hr />
          <p>{company?.name} | CIN: {company?.cin || 'N/A'}</p>
          <p>Email: {company?.email || 'N/A'} | {company?.contact || 'N/A'}</p>
        </div>
      </div>
    </div>
  );
};

export default PromotionLetter;